import { useState } from 'react';
import type { FormInstance } from 'antd';

import { ICustomRequirements } from '@partsbadger/types';

import QuoteStore from '../../stores/QuoteStore';
import { buildRequirementsPayload, describeRequirementChanges } from '../../utils/quoteRequirements';

interface Options {
    form?: FormInstance;
    onApply: (payload: Record<string, unknown>) => Promise<unknown>;
}

/**
 * Requirement edits stay local until staff apply them: every quote update reprices all lines on the server,
 * so the whole draft goes out in one request. Use from an observer component so the saved list stays current.
 */
export const useQuoteRequirementsDraft = ({ form, onApply }: Options) => {
    const savedRequirements = QuoteStore.quote?.custom_requirements ?? [];
    const [draft, setDraft] = useState<ICustomRequirements[] | null>(null);
    const [applying, setApplying] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Bumped when the draft is reset, so uncontrolled inputs (file lists) start over from the saved values.
    const [version, setVersion] = useState(0);

    const requirements = draft ?? savedRequirements;
    const changes = describeRequirementChanges(savedRequirements, requirements);

    const change = (next: ICustomRequirements[]) => {
        setDraft(next);
        setError(null);
    };

    const discard = () => {
        setDraft(null);
        setError(null);
        setUploading(false);
        setVersion(current => current + 1);
    };

    const apply = async () => {
        if (!changes.count || applying || uploading) return false;

        const payload = buildRequirementsPayload(requirements, QuoteStore.additional_requirements);
        setApplying(true);
        setError(null);
        try {
            await onApply(payload);
            const { custom_requirements, ...flags } = payload;
            form?.setFieldsValue({ additional_requirements: custom_requirements, ...flags });
            setDraft(null);
            setVersion(current => current + 1);
            return true;
        } catch {
            setError('Changes couldn’t be saved. Try again.');
            return false;
        } finally {
            setApplying(false);
        }
    };

    return {
        savedRequirements,
        requirements,
        changes,
        applying,
        uploading,
        error,
        version,
        change,
        discard,
        apply,
        setUploading,
    };
};

export type QuoteRequirementsDraft = ReturnType<typeof useQuoteRequirementsDraft>;
